import classNames from 'classnames';
import { t } from 'ttag';
import queryString from 'query-string';

import SearchIcon from '@svg/search.svg';
import CategoryIcon from '@svg/category.svg';
import { getPathWithoutLang } from '@utils/i18n';
import { FILTER_QUERIES } from '@constants';
import { Button } from '@ui/Button';

import s from './SearchSuggests.sss';

export interface Suggest {
  id: string,
  search: string,
  replacementOfSuggest?: {
    slug: string,
  } | null,
}

export interface Category {
  id: string | number,
  breadcrumbs: Omit<Category, 'breadcrumbs' | 'url'>[]
  name: string,
  url: string,
}

interface Props {
  className?: string,
  searchText?: string,
  suggests: Suggest[],
  categoriesSuggests: Category[],
  suggestsInCategories: Category[],
  onSuggestClick: (suggest: Suggest) => void,
  onCategorySuggestClick?: (category: Category) => void,
  onSuggestInCategoryClick?: (category: Category) => void,
}

export const SearchSuggests: React.FC<Props> = ({
  className,
  searchText,
  suggests,
  categoriesSuggests,
  suggestsInCategories,
  onSuggestClick,
  onCategorySuggestClick,
  onSuggestInCategoryClick,
}) => {
  return (
    <div className={classNames(s.root, className)}>
      {!!suggests?.length && (
        <section className={s.section}>
          <ul className={s.list}>
            {suggests.map((suggest) => {
              const regex = new RegExp(`(${searchText})`, 'gi');
              const text = suggest.search.replace(regex, '<span>$1</span>');
              return (
                <li
                  key={suggest.id}
                  className={s.item}
                >
                  <Button
                    className={s.button}
                    theme='text'
                    onClick={() => onSuggestClick(suggest)}
                  >
                    <p
                      className={classNames(s.buttonText, s.buttonTextBold)}
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {!!categoriesSuggests?.length && (
        <section className={s.section}>
          <p className={s.title}>
            {t`Перейти в категорію`}
          </p>

          <ul className={s.list}>
            {categoriesSuggests.map((category) => {
              const breadcrumbs = category.breadcrumbs
                .slice(0, category.breadcrumbs.length - 1)
                .map((c) => c.name)
                .join('/');

              return (
                <li
                  key={category.id}
                  className={s.item}
                >
                  <Button
                    className={s.button}
                    theme='text'
                    to={getPathWithoutLang(category.url)}
                    onClick={() => onCategorySuggestClick?.(category)}
                  >
                    <CategoryIcon
                      className={s.icon}
                      width='24'
                      height='24'
                    />
                    <p className={s.buttonText}>
                      <span className={s.categoryName}>
                        {category.name}
                      </span>

                      {!!breadcrumbs.length && (
                        <span className={s.breadcrumbs}>
                          {breadcrumbs}
                        </span>
                      )}
                    </p>
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {!!suggestsInCategories?.length && (
        <section className={s.section}>
          <p className={s.title}>
            {t`Шукати «${searchText}» в категоріях`}
          </p>

          <ul className={s.list}>
            {suggestsInCategories.map((category) => {
              const breadcrumbs = category.breadcrumbs
                .slice(0, category.breadcrumbs.length - 1)
                .map((c) => c.name)
                .join('/');

              return (
                <li
                  key={category.id}
                  className={s.item}
                >
                  <Button
                    className={s.button}
                    theme='text'
                    to={{
                      pathname: getPathWithoutLang(category.url),
                      search: queryString.stringify({
                        [FILTER_QUERIES.SEARCH_TEXT]: searchText,
                      })
                    }}
                    onClick={() => onSuggestInCategoryClick?.(category)}
                  >
                    <SearchIcon
                      className={s.icon}
                      width='24'
                      height='24'
                    />
                    <p className={s.buttonText}>
                      <span className={s.categoryName}>
                        {category.name}
                      </span>

                      {!!breadcrumbs.length && (
                        <span className={s.breadcrumbs}>
                          {breadcrumbs}
                        </span>
                      )}
                    </p>
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
